import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebaseConfig";
import {
  doc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  arrayUnion
} from "firebase/firestore";
import promptQuestions from "../promptQuestions";

export default function PromptQuestionsGame() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const isCreator = localStorage.getItem("isCreator") === "true";
  const myName = localStorage.getItem("spyfallName") || "";

  const [room, setRoom] = useState(null);
  const [draftAnswer, setDraftAnswer] = useState("");

  // Listen in real‑time to the room doc
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "rooms", roomId), snap => {
      if (!snap.exists()) {
        navigate("/promptquestions");
        return;
      }
      const data = snap.data();
      if (data.started === false) {
        navigate("/promptquestions");
        return;
      }
      setRoom(data);
    });
    return unsub;
  }, [roomId, navigate]);

  if (!room || !myName) return <p>Loading…</p>;

  // Helpers
  const role = room.roles[myName];
  const isSpy = role === "spy";
  const imposterPrompt = room.question.imposterPrompt;
  const realPrompt = room.question.realPrompt;

  const totalPlayers = room.players.length;
  const answers = room.answers || {};
  const revealed = room.revealedAnswers || [];
  const allAnswered = Object.keys(answers).length === totalPlayers;

  // 1) Submit answer
  const submitAnswer = async () => {
    if (!draftAnswer.trim()) return;
    await updateDoc(doc(db, "rooms", roomId), {
      answers: {
        ...answers,
        [myName]: draftAnswer.trim()
      }
    });
    setDraftAnswer("");
  };

  // 2) Creator: reveal next answer
  const revealNext = async () => {
    const idx = revealed.length; // next index
    if (idx < totalPlayers) {
      const nextPlayer = room.players[idx].name;
      await updateDoc(doc(db, "rooms", roomId), {
        revealedAnswers: arrayUnion(nextPlayer)
      });
    }
  };

  // 3) Creator: reveal real question
  const revealQuestion = async () => {
    await updateDoc(doc(db, "rooms", roomId), {
      showRealQuestion: true
    });
  };

  // 4) Play again
  const playAgain = async () => {
    const newQ = promptQuestions[
      Math.floor(Math.random() * promptQuestions.length)
    ];
    const spyIdx = Math.floor(Math.random() * totalPlayers);
    const newRoles = {};
    room.players.forEach((p, i) => {
      newRoles[p.name] = i === spyIdx ? "spy" : "agent";
    });
    await updateDoc(doc(db, "rooms", roomId), {
      question: newQ,
      roles: newRoles,
      answers: {},
      revealedAnswers: [],
      showRealQuestion: false
    });
  };

  // 5) End game
  const endGame = async () => {
    await updateDoc(doc(db, "rooms", roomId), { started: false });
    await deleteDoc(doc(db, "rooms", roomId));
    navigate("/promptquestions");
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Prompt Questions</h1>
      <h2 style={styles.subheader}>Welcome, {myName}</h2>
      <p style={styles.promptText}>
        Your prompt: <span style={styles.promptHighlight}>{isSpy ? imposterPrompt : realPrompt}</span>
      </p>

      {/* Answer form */}
      {!answers[myName] && (
        <div style={styles.answerBox}>
          <textarea
            value={draftAnswer}
            onChange={e => setDraftAnswer(e.target.value)}
            placeholder="Write your answer…"
            style={styles.textarea}
          />
          <button style={styles.submit} onClick={submitAnswer}>
            Submit Answer
          </button>
        </div>
      )}

      {/* Wait notice */}
      {allAnswered && !isCreator && !room.showRealQuestion && (
        <p style={styles.waitText}>All answered—waiting for reveal…</p>
      )}

      {/* Revealed answers (everyone sees these) */}
      {revealed.length > 0 && (
        <div style={styles.revealedContainer}>
          <h3 style={styles.revealHeader}>Revealed Answers</h3>
          {revealed.map((name, i) => (
            <div key={i} style={styles.revealItem}>
              <strong>{name}:</strong> <em>{answers[name]}</em>
            </div>
          ))}
        </div>
      )}

      {/* Creator reveal controls */}
      {isCreator && allAnswered && revealed.length < totalPlayers && (
        <button style={styles.revealBtn} onClick={revealNext}>
          Reveal Next Answer
        </button>
      )}

      {/* Creator: reveal question once all answers shown */}
      {isCreator &&
        revealed.length === totalPlayers &&
        !room.showRealQuestion && (
          <button style={styles.revealBtn} onClick={revealQuestion}>
            Reveal Real Question
          </button>
        )}

      {/* Real question visible to all after reveal */}
      {room.showRealQuestion && (
        <div style={styles.realQuestionBox}>
          <h3 style={styles.realQuestionHeader}>Real Question</h3>
          <p style={styles.realQuestion}>{realPrompt}</p>
        </div>
      )}

      {/* Creator bottom controls */}
      {isCreator && (
        <div style={styles.creatorControls}>
          <button style={styles.playAgain} onClick={playAgain}>
            🔁 Play Again
          </button>
          <button style={styles.end} onClick={endGame}>
            ❌ End Game
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    textAlign: "center",
    fontFamily: "'Patrick Hand'",
    margin: "2rem",
    background: "#faf3e0",
    padding: "2rem",
    borderRadius: "12px",
    boxShadow: "0px 4px 10px rgba(0,0,0,0.1)"
  },
  header: {
    fontSize: "2.5rem",
    color: "#333"
  },
  subheader: {
    fontSize: "1.5rem",
    color: "#555"
  },
  promptText: {
    fontSize: "1.1rem",
    margin: "1rem 0"
  },
  promptHighlight: {
    background: "#fff7c0",
    padding: "0.2rem 0.4rem",
    borderRadius: "4px",
    fontStyle: "italic"
  },
  answerBox: {
    marginTop: "1rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  textarea: {
    width: "80%",
    maxWidth: "600px",
    height: "140px",
    marginBottom: "1rem",
    padding: "1rem",
    fontSize: "1rem",
    borderRadius: "12px",
    border: "2px solid #ffd97d",
    background: "#fffaf0",
    boxShadow: "inset 2px 2px 5px rgba(0,0,0,0.05)",
    fontFamily: "'Patrick Hand'",
    outline: "none",
    resize: "vertical"
  },
  submit: {
    padding: "0.6rem 1.2rem",
    background: "#ffb74d",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "1rem",
    fontFamily: "'Patrick Hand'"
  },
  waitText: {
    fontStyle: "italic",
    color: "#777"
  },
  revealedContainer: {
    marginTop: "2rem",
    background: "#fffbea",
    padding: "1rem",
    borderRadius: "8px"
  },
  revealHeader: {
    fontSize: "1.3rem",
    marginBottom: "0.5rem"
  },
  revealItem: {
    textAlign: "left",
    margin: "0.3rem 0",
    fontFamily: "'Patrick Hand'"
  },
  revealBtn: {
    display: "inline-block",
    margin: "1rem 0",
    padding: "0.5rem 1rem",
    background: "#ffa726",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontFamily: "'Patrick Hand'"
  },
  realQuestionBox: {
    marginTop: "2rem",
    background: "#fff3e0",
    padding: "1rem",
    borderRadius: "8px",
    boxShadow: "0px 2px 5px rgba(0,0,0,0.05)"
  },
  realQuestionHeader: {
    fontSize: "1.2rem",
    marginBottom: "0.5rem"
  },
  realQuestion: {
    fontFamily: "'Patrick Hand'",
    fontStyle: "italic"
  },
  creatorControls: {
    marginTop: "2rem"
  },
  playAgain: {
    padding: "0.5rem 1rem",
    background: "#81c784",
    border: "none",
    borderRadius: "8px",
    color: "#fff",
    cursor: "pointer",
    marginRight: 10,
    fontFamily: "'Patrick Hand'"
  },
  end: {
    padding: "0.5rem 1rem",
    background: "#e57373",
    border: "none",
    borderRadius: "8px",
    color: "#fff",
    cursor: "pointer",
    fontFamily: "'Patrick Hand'"
  }
};
